/**
 * The one judgement the revolving-door section makes about a filing.
 *
 * `position` is the shortest of the wordings a lobbyist filed for one post,
 * and on nineteen rows of the corpus that shortest wording is the member's
 * name with no job title attached to it — a filer wrote "Rep. Mario
 * Diaz-Balart" into the field asking what job they held. The section labels
 * every wording as a declared post, which stops that row reading as an
 * identity; this decides whether it can also say, on the row, that the filing
 * gave no title.
 *
 * Kept here rather than in the component because it is the only thing on the
 * section a reader could not check against the filing themselves, and it
 * should be readable and tested on its own.
 */

/**
 * Words that qualify a name rather than describe a job.
 *
 * Anything left over once these and the member's own name are removed is a
 * post, however short: "Intern" is a job somebody held.
 */
const NOT_A_POST = new Set([
  "rep",
  "reps",
  "representative",
  "congressman",
  "congresswoman",
  "congressmember",
  "sen",
  "senator",
  "hon",
  "honorable",
  "honourable",
  "the",
  "office",
  "of",
  "for",
  "former",
  "us",
  "u",
  "s",
  "mr",
  "mrs",
  "ms",
  "dr",
  "jr",
  "sr",
  "ii",
  "iii",
]);

/** Lowercase words, accents dropped, so a filing writing "Velazquez" is
 *  measured against a roster holding "Velázquez". */
function words(text: string): string[] {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(/[^a-z]+/)
    .filter(Boolean);
}

/**
 * Whether a declared post is the member's name and no job at all.
 *
 * Deliberately one-sided. It answers yes only when every word of the wording
 * is either part of the member's name as the roster holds it or a title
 * attaching to that name, so "Rep. Dan Kildee" against a roster reading
 * "Daniel T. Kildee" comes back no and the row goes without the extra line. A
 * false no costs a sentence the label in front of the wording already covers;
 * a false yes would tell a reader a filer left out something they wrote.
 */
export function namesNoPost(position: string, member: string | null): boolean {
  const named = new Set(words(member ?? ""));
  if (named.size === 0) return false;
  const filed = words(position);
  return filed.length > 0 && filed.every((word) => named.has(word) || NOT_A_POST.has(word));
}
